import type { JSX, MouseEvent } from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { type UseFieldConfig, useField, useForm } from "react-final-form";
import { type UseFieldArrayConfig, useFieldArray } from "react-final-form-arrays";
import styled from "styled-components";
import { currentColors } from "../../colors";
import t from "../../translations";
import CrossIcon from "../icons/CrossIcon";
import ImagePinIcon from "../icons/ImagePinIcon";

export interface IImageAnswerValue {
    clickX: number;
    clickY: number;
    answer: string;
}

interface IOwnProps {
    name: string;
    image: {
        id: string;
        mediaType: string;
        src: string;
    };
    style?: "square" | "rounded";
}

type IProps = IOwnProps & UseFieldArrayConfig<IImageAnswerValue>;

const Container = styled.div`
    display: grid;
    gap: 1rem;
    margin-bottom: 1rem;
`;

const ImageContainer = styled.div<{
    $topMargin: number;
}>`
    position: relative;
    display: inline-block;
    justify-self: center;
    user-select: none;
    margin-top: ${(props) => props.$topMargin}px;
`;

const StyledImage = styled.img`
    display: block;
    max-width: 100%;
    height: auto;
`;

const PinContainer = styled.div<{
    $left: number;
    $top: number;
}>`
    position: absolute;
    left: ${(props) => props.$left}px;
    top: ${(props) => props.$top}px;
    width: 30px;
    height: 60px;
    transform: translate(-50%, -100%);
    cursor: pointer;
    pointer-events: auto;
`;

const StyledPin = styled(ImagePinIcon)`
    width: 100%;
    height: 100%;
`;

const AnswerContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const AnswerRow = styled.div`
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: start;
    gap: 0.5rem;
`;

const RemoveCross = styled(CrossIcon)`
    width: 1rem;
    height: 1rem;
    cursor: pointer;
    margin-top: 10px;
`;

const AnswerPin = styled(ImagePinIcon)`
    width: 30px;
    height: 60px;
`;

const ErrorLabel = styled.small<{ $style?: "square" | "rounded"; $hasInput?: boolean }>`
    display: block;
    font-size: .75rem;
    font-style: italic;
    font-weight: normal;
    padding: .375rem .5625rem .5625rem;
    background: ${currentColors.error};
    color: white;
    border-radius: ${(props) => {
        if (props.$style === "rounded") {
            return props.$hasInput ? "0 0 0.5rem 0.5rem" : "0.5rem";
        }
        return "0";
    }};
    border: ${(props) => (props.$style === "rounded" ? `1px solid ${currentColors.text}` : "none")};
    margin-top: ${(props) => (props.$style === "rounded" && props.$hasInput ? "-1px" : "0")};
`;

const StyledInput = styled.input<{ $style?: "square" | "rounded"; $hasError?: boolean }>`
    margin: 0;
    background: ${(props) => (props.$style === "rounded" ? "white" : "initial")};
    border: ${(props) => (props.$style === "rounded" ? `1px solid ${currentColors.text}` : "initial")};
    border-radius: ${(props) => {
        if (props.$style === "rounded") {
            return props.$hasError ? "0.5rem 0.5rem 0 0" : "0.5rem";
        }
        return "0";
    }};
`;

function Input({
    name,
    placeholder,
    style = "square",
    ...props
}: { name: string; placeholder: string; style?: "square" | "rounded" } & UseFieldConfig<string>): JSX.Element {
    const { input, meta } = useField<string, HTMLInputElement>(name, props);
    const hasError = meta.touched && meta.error;

    return (
        <div>
            <StyledInput {...input} placeholder={placeholder} $style={style} $hasError={hasError} />
            {hasError && (
                <ErrorLabel $style={style} $hasInput={true}>
                    {meta.error}
                </ErrorLabel>
            )}
        </div>
    );
}

function ImageSelectionAnswersInput({ name, image, style = "square", ...props }: IProps): JSX.Element {
    const {
        fields,
        meta: { touched, invalid, error },
    } = useFieldArray(name, props);
    const form = useForm();
    const imageRef = useRef<HTMLImageElement>(null);
    const [imageLoaded, setImageLoaded] = useState(false);

    if (image.mediaType !== "Image") {
        return null;
    }

    const getImageCoordinates = useCallback((event: MouseEvent<HTMLImageElement>) => {
        if (!imageRef.current) {
            return { x: 0, y: 0 };
        }

        const rect = imageRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(imageRef.current.offsetWidth, event.clientX - rect.left));
        const y = Math.max(0, Math.min(imageRef.current.offsetHeight, event.clientY - rect.top));

        return { x, y };
    }, []);

    const normalizeCoordinates = useCallback((x: number, y: number) => {
        if (!imageRef.current) {
            return { normalizedX: 0, normalizedY: 0 };
        }

        const normalizedX = Math.max(0, Math.min(1, x / imageRef.current.offsetWidth));
        const normalizedY = Math.max(0, Math.min(1, y / imageRef.current.offsetHeight));

        return { normalizedX, normalizedY };
    }, []);

    const denormalizeCoordinates = useCallback((normalizedX: number, normalizedY: number) => {
        if (!imageRef.current) {
            return { x: 0, y: 0 };
        }

        const x = normalizedX * imageRef.current.offsetWidth;
        const y = normalizedY * imageRef.current.offsetHeight;

        return { x, y };
    }, []);

    const handleImageClick = useCallback(
        (event: MouseEvent<HTMLImageElement>) => {
            event.preventDefault();
            const { x, y } = getImageCoordinates(event);
            const { normalizedX: clickX, normalizedY: clickY } = normalizeCoordinates(x, y);

            const newAnswer: IImageAnswerValue = {
                clickX,
                clickY,
                answer: "",
            };

            fields.push(newAnswer);
            form.blur(name);
        },
        [getImageCoordinates, normalizeCoordinates, fields, form, name],
    );

    const handleDeleteAnswer = useCallback(
        (index: number) => {
            const answer = fields.value[index] as IImageAnswerValue;

            // Check if answer is not empty and confirm deletion
            if (answer.answer && answer.answer.trim() !== "") {
                const confirmed = window.confirm(
                    t("shared.image-selection-areas-input.confirm-delete-with-explanation"),
                );
                if (!confirmed) {
                    return;
                }
            }

            fields.remove(index);
        },
        [fields],
    );

    const handleImageLoad = useCallback(() => {
        setImageLoaded(true);
    }, []);

    // Calculate required top margin to prevent pins from sticking out above the image
    const topMargin = useMemo(() => {
        if (!imageLoaded || !imageRef.current || fields.length === 0) {
            return 0;
        }

        const PIN_HEIGHT = 60;
        let maxPinOverflow = 0;

        for (let i = 0; i < fields.length; i++) {
            const answer = fields.value[i] as IImageAnswerValue;
            const { y } = denormalizeCoordinates(answer.clickX, answer.clickY);
            // Pin is positioned with transform: translate(-50%, -100%), so it extends PIN_HEIGHT pixels above its position
            const pinTop = y - PIN_HEIGHT;
            if (pinTop < 0) {
                maxPinOverflow = Math.max(maxPinOverflow, Math.abs(pinTop));
            }
        }

        return maxPinOverflow;
    }, [imageLoaded, fields.length, fields.value, denormalizeCoordinates]);

    return (
        <Container>
            <ImageContainer $topMargin={topMargin}>
                <StyledImage
                    ref={imageRef}
                    src={image.src}
                    alt={t("shared.image-selection-answers-input.alt-selection-area-image")}
                    draggable={false}
                    onLoad={handleImageLoad}
                    onClick={handleImageClick}
                />

                {fields.map((field, index) => {
                    const answer = fields.value[index] as IImageAnswerValue;
                    if (!imageRef.current || !imageLoaded) {
                        return null;
                    }

                    const { x: pinX, y: pinY } = denormalizeCoordinates(answer.clickX, answer.clickY);

                    return (
                        <PinContainer key={field} $left={pinX} $top={pinY} onClick={() => handleDeleteAnswer(index)}>
                            <StyledPin number={index + 1} />
                        </PinContainer>
                    );
                })}
            </ImageContainer>
            {touched && invalid && typeof error === "string" && (
                <ErrorLabel $style={style} $hasInput={false}>
                    {error}
                </ErrorLabel>
            )}

            {fields.length > 0 && (
                <AnswerContainer>
                    {fields.map((field, index) => {
                        return (
                            <AnswerRow key={field}>
                                <AnswerPin number={index + 1} />
                                <Input
                                    name={`${field}.answer`}
                                    placeholder={t("shared.image-selection-answers-input.placeholder-explanation", {
                                        number: index + 1,
                                    })}
                                    style={style}
                                />
                                <RemoveCross onClick={() => handleDeleteAnswer(index)} />
                            </AnswerRow>
                        );
                    })}
                </AnswerContainer>
            )}
        </Container>
    );
}

export default ImageSelectionAnswersInput;
